import { Select } from 'antd';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { Line, Pie } from 'react-chartjs-2';

import { useAuth } from '../api/AuthContext.js';
import { fetchAllEventsByOwner } from '../api/fetchAllEvents.js';
import { queryAttendance } from '../api/queryAttendance.js';
import { queryAttendanceOverTime, queryResponseStats } from '../api/queryAttendanceCharts.js';
import * as utils from '../utils/utils.js';

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend
);

let comboEvents = [];

function Statistics() {
    const auth = useAuth();

    const [selectedItems, setSelectedItems] = useState([]);
    const [dateRange, setDateRange] = useState(null);
    const [num_participants, setNumParticipants] = useState(10);
    const [queryResults, setQueryResults] = useState([]);
    const [attendanceOverTimeData, setAttendanceOverTimeData] = useState(null);
    const [responseStatsData, setResponseStatsData] = useState(null);
    const [isLoadingCharts, setIsLoadingCharts] = useState(false);

    const filteredOptions = comboEvents.filter((item) => {
        return !selectedItems.includes(item);
    });

    const fetchEvents = async (owner_id) => {
        try {
            const result = await fetchAllEventsByOwner(owner_id);

            comboEvents = [];
            let selected = [];
            result.forEach((item) => {
                if (item.type !== utils.SINGLE_EVENT) {
                    comboEvents.push({ id: item.event_id, name: item.name });
                    selected.push(item.event_id);
                }
            });
            setSelectedItems(selected);
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        fetchEvents(auth.currentUser.user_id);
        setDateRange('thisMonth');
    }, [auth.currentUser.user_id]);

    const getDateRange = () => {
        const dateFormat = 'YYYY-MM-DD HH:mm:ss';
        const now = new dayjs();
        let startDate, endDate;

        switch (dateRange) {
            case 'thisMonth':
                startDate = now.startOf('month').format(dateFormat);
                endDate = now.endOf('month').format(dateFormat);
                break;
            case 'thisYear':
                startDate = now.startOf('year').format(dateFormat);
                endDate = now.endOf('year').format(dateFormat);
                break;
            case 'lastMonth':
                startDate = now
                    .subtract(1, 'month')
                    .startOf('month')
                    .format(dateFormat);
                endDate = now
                    .subtract(1, 'month')
                    .endOf('month')
                    .format(dateFormat);
                break;
            case 'lastYear':
                startDate = now
                    .subtract(1, 'year')
                    .startOf('year')
                    .format(dateFormat);
                endDate = now
                    .subtract(1, 'year')
                    .endOf('year')
                    .format(dateFormat);
                break;
            default:
                startDate = null;
                endDate = null;
        }

        return { startDate, endDate };
    };

    const queryChartData = async () => {
        if (selectedItems.length === 0) return;

        setIsLoadingCharts(true);
        const { startDate, endDate } = getDateRange();

        try {
            // Fetch attendance over time
            const overTimeData = await queryAttendanceOverTime(
                startDate,
                endDate,
                selectedItems
            );

            // Fetch response statistics
            const responseData = await queryResponseStats(
                startDate,
                endDate,
                selectedItems
            );

            // Process attendance over time data for chart
            const labels = overTimeData.map((item) =>
                dayjs(item.date).format('MMM D')
            );
            const confirmedData = overTimeData.map((item) =>
                parseInt(item.confirmedAttendances) || 0
            );

            setAttendanceOverTimeData({
                labels,
                datasets: [
                    {
                        label: 'Confirmed Attendances',
                        data: confirmedData,
                        borderColor: 'rgb(75, 192, 192)',
                        backgroundColor: 'rgba(75, 192, 192, 0.2)',
                        tension: 0.3,
                    },
                ],
            });

            // Process response stats data for chart
            const responseLabels = [];
            const responseCounts = [];
            const backgroundColors = [];

            responseData.forEach((item) => {
                if (item.status === 'confirmed') {
                    responseLabels.push('Confirmed');
                    responseCounts.push(parseInt(item.count));
                    backgroundColors.push('rgba(75, 192, 192, 0.6)');
                } else if (item.status === 'declined') {
                    responseLabels.push('Declined');
                    responseCounts.push(parseInt(item.count));
                    backgroundColors.push('rgba(255, 99, 132, 0.6)');
                } else if (item.status === 'no_response') {
                    responseLabels.push('No Response');
                    responseCounts.push(parseInt(item.count));
                    backgroundColors.push('rgba(201, 203, 207, 0.6)');
                }
            });

            setResponseStatsData({
                labels: responseLabels,
                datasets: [
                    {
                        data: responseCounts,
                        backgroundColor: backgroundColors,
                        borderColor: backgroundColors.map(color => color.replace('0.6', '1')),
                        borderWidth: 1,
                    },
                ],
            });
        } catch (error) {
            console.error('Error fetching chart data:', error);
        } finally {
            setIsLoadingCharts(false);
        }
    };

    const queryAttendanceStats = async () => {
        const { startDate, endDate } = getDateRange();

        const response = await queryAttendance(
            startDate,
            endDate,
            selectedItems,
            num_participants
        );

        let results = [];
        response.forEach((item) => {
            results.push(
                item.attendanceCount.toString().padStart(3, ' ') +
                    ' ' +
                    item.name +
                    (item.nickname !== '' ? ' / ' + item.nickname : '')
            );
        });
        setQueryResults(results);

        // Also fetch chart data
        queryChartData();
    };

    const listItems = queryResults.map((participant, i) => (
        <li key={i}>
            <p>
                <b>{participant}</b>
            </p>
        </li>
    ));

    return (
        <>
            <h1>Statistics</h1> <br />
            <div className="block">
                <h2>Attendance report</h2>
                <label className="label">Selected events:</label>
                <Select
                    mode="multiple"
                    placeholder="Select events to continue"
                    value={selectedItems}
                    onChange={setSelectedItems}
                    style={{ width: '100%' }}
                    options={filteredOptions.map((item) => ({
                        value: item.id,
                        label: item.name,
                    }))}
                />
                <br />
                <br />
                <label className="label">Date range:</label>
                <Select
                    style={{ width: 120 }}
                    id="dateRange"
                    popupClassName="repeatDayClass"
                    value={dateRange}
                    onChange={setDateRange}
                    options={[
                        {
                            id: 'thisMonth',
                            value: 'thisMonth',
                            label: 'This month',
                        },
                        {
                            id: 'thisYear',
                            value: 'thisYear',
                            label: 'This year',
                        },
                        {
                            id: 'lastMonth',
                            value: 'lastMonth',
                            label: 'Last month',
                        },
                        {
                            id: 'lastYear',
                            value: 'lastYear',
                            label: 'Last year',
                        },
                    ]}
                />
                <br />
                <br />
                <button
                    id="queryAttendanceButton"
                    className="button is-primary"
                    disabled={selectedItems.length === 0}
                    onClick={() => queryAttendanceStats()}
                >
                    {isLoadingCharts ? 'Loading...' : 'Generate Report & Charts'}
                </button>
                <br />
                <br />

                {/* Charts Section */}
                {(attendanceOverTimeData || responseStatsData) && (
                    <div style={{ marginBottom: '40px' }}>
                        <h3 style={{ marginBottom: '20px', fontSize: '20px', fontWeight: '600' }}>
                            Visual Analytics
                        </h3>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
                            gap: '24px',
                            marginBottom: '40px'
                        }}>
                            {/* Attendance Over Time Chart */}
                            {attendanceOverTimeData && (
                                <div style={{
                                    padding: '20px',
                                    backgroundColor: 'white',
                                    borderRadius: '8px',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                }}>
                                    <h4 style={{
                                        marginBottom: '16px',
                                        fontSize: '16px',
                                        fontWeight: '600',
                                        color: '#1f2937'
                                    }}>
                                        Attendance Over Time
                                    </h4>
                                    <Line
                                        data={attendanceOverTimeData}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: true,
                                            plugins: {
                                                legend: {
                                                    position: 'top',
                                                },
                                                tooltip: {
                                                    mode: 'index',
                                                    intersect: false,
                                                },
                                            },
                                            scales: {
                                                y: {
                                                    beginAtZero: true,
                                                    ticks: {
                                                        stepSize: 1,
                                                    },
                                                },
                                            },
                                        }}
                                    />
                                </div>
                            )}

                            {/* Response Statistics Chart */}
                            {responseStatsData && (
                                <div style={{
                                    padding: '20px',
                                    backgroundColor: 'white',
                                    borderRadius: '8px',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                }}>
                                    <h4 style={{
                                        marginBottom: '16px',
                                        fontSize: '16px',
                                        fontWeight: '600',
                                        color: '#1f2937'
                                    }}>
                                        Participant Responses
                                    </h4>
                                    <div style={{ maxWidth: '300px', margin: '0 auto' }}>
                                        <Pie
                                            data={responseStatsData}
                                            options={{
                                                responsive: true,
                                                maintainAspectRatio: true,
                                                plugins: {
                                                    legend: {
                                                        position: 'bottom',
                                                    },
                                                    tooltip: {
                                                        callbacks: {
                                                            label: function(context) {
                                                                const label = context.label || '';
                                                                const value = context.parsed || 0;
                                                                const total = context.dataset.data.reduce((a, b) => a + b, 0);
                                                                const percentage = ((value / total) * 100).toFixed(1);
                                                                return `${label}: ${value} (${percentage}%)`;
                                                            }
                                                        }
                                                    },
                                                },
                                            }}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Top Participants List */}
                {queryResults.length > 0 && (
                    <div>
                        <h3 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: '600' }}>
                            Top {num_participants} Participants
                        </h3>
                        <label className="label">Max rows to show:</label>
                        <input
                            className="input"
                            type="number"
                            id="num_participants"
                            value={num_participants}
                            onChange={(e) =>
                                utils.ensureNonNegativeAndForwardTo(
                                    e.target.value,
                                    setNumParticipants
                                )
                            }
                            pattern="\d*"
                            style={{ maxWidth: '200px', marginBottom: '16px' }}
                        />
                        <ul>{listItems}</ul>
                    </div>
                )}
            </div>
        </>
    );
}

export default Statistics;
